import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useThemeStore } from '../stores/useThemeStore';
import { useAuthStore } from '../stores/useAuthStore';
import { Switch } from '@/components/ui/switch';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import iconVib3 from '@/assets/icon-vib3.svg';
import DeleteDataButton from './DeleteDataButton';

function useAutoHideHeader() {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 50) {
        setVisible(true);
      } else if (y < lastY.current) {
        setVisible(true);
      } else {
        setVisible(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Set CSS variable for sticky tab offset
  useEffect(() => {
    const h = headerRef.current?.offsetHeight || 0;
    document.documentElement.style.setProperty('--header-h', `${visible ? h : 0}px`);
  }, [visible]);

  return { visible, headerRef };
}

// Report generation is folded into the single-run Analysis step (the backend
// auto-chains analysis → refine → report), so there is no separate
// "Generate Report" step anymore. /generate-report redirects to /processing.
//
// The report itself is not a step: by the time it exists the flow is over, and
// the stepper is hidden there anyway (see `showStepper`).
const STEPS = [
  { label: 'Sign', path: '/sign' },
  { label: 'Connect', path: '/connect' },
  { label: 'Collect', path: '/collect' },
  { label: 'Confirm', path: '/confirm' },
  { label: 'Analysis', path: '/processing' },
] as const;

const PATH_ORDER = ['/sign', '/connect', '/collect', '/confirm', '/processing'] as const;

function getStepState(stepPath: string, currentPath: string, _search: string) {
  const stepIdx = PATH_ORDER.indexOf(stepPath as typeof PATH_ORDER[number]);
  // Strip /:chatId suffix for matching (e.g. /report/abc123 → /report)
  const basePath = '/' + currentPath.split('/').filter(Boolean)[0];
  const effectiveIdx = PATH_ORDER.indexOf(basePath as typeof PATH_ORDER[number]);
  if (effectiveIdx < 0) return 'pending';
  if (stepIdx < effectiveIdx) return 'completed';
  if (stepIdx === effectiveIdx) return 'active';
  return 'pending';
}

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();
  const { token, signOut } = useAuthStore();
  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };
  const isLanding = location.pathname === '/';
  const basePath = '/' + location.pathname.split('/').filter(Boolean)[0];
  const isReport = basePath === '/report';
  // Legal docs render their own landing-style nav/footer, so suppress the app header.
  const isStandalone = isLanding || basePath === '/privacy' || basePath === '/terms';
  // Stepper only on the actual workflow steps — not on landing, report, login,
  // or static pages (privacy/terms), which would otherwise show an all-pending bar.
  const showStepper = !isReport && PATH_ORDER.includes(basePath as typeof PATH_ORDER[number]);
  // Collect and Analysis both run unattended, writing rows the whole time —
  // collect for a couple of minutes, the audit for up to fifteen. Wiping the
  // account mid-run would delete data the run is still producing (and the run
  // would keep writing into the void afterwards), so the button is out of
  // reach for exactly as long as either lasts.
  const canDeleteData = basePath !== '/collect' && basePath !== '/processing';

  const { visible: headerVisible, headerRef } = useAutoHideHeader();

  return (
    <div className="flex min-h-screen flex-col">
      {!isStandalone && (
      <header
        ref={headerRef}
        className={cn(
          "sticky top-0 z-50 flex items-center justify-between border-b bg-card px-3 py-1.5 sm:px-6 sm:py-2 transition-transform duration-300",
          headerVisible ? "translate-y-0" : "-translate-y-full"
        )}
      >
        {/* Logo — clickable, goes home */}
        <Link to="/" className="shrink-0">
          <img src={iconVib3} alt="Vib3" className="h-5 w-auto sm:h-6" />
        </Link>

        {/* Report tabs — shown on report page next to logo */}
        {isReport && <ReportHeaderNav />}

        {/* Stepper — centered, hidden on report page */}
        {showStepper && (
          <div className="flex items-center gap-0">
            {STEPS.map((step, i) => {
              const state = getStepState(step.path, location.pathname, location.search);
              return (
                <div key={step.path} className="flex items-center">
                  {i > 0 && (
                    <div className={cn(
                      "mx-1 h-0.5 w-4 transition-colors sm:mx-3 sm:w-12",
                      state === 'completed' || state === 'active' ? 'bg-primary' : 'bg-border'
                    )} />
                  )}
                  {/* Stepper is purely informational — steps are never
                      clickable, even when completed. The user MUST stay on
                      the page they were navigated to (via the analysis /
                      refine flow); jumping back to /connect or /processing
                      mid-pipeline would break server-driven state and
                      potentially restart background tasks. */}
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] text-[10px] font-semibold transition-colors",
                      state === 'active' && "border-primary bg-primary text-primary-foreground",
                      state === 'completed' && "border-primary bg-primary text-primary-foreground",
                      state === 'pending' && "border-border bg-transparent text-muted-foreground"
                    )}>
                      {state === 'completed' ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className={cn(
                      "hidden text-xs font-medium sm:inline",
                      state === 'active' ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {step.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete data + theme toggle + logout */}
        <div className="hidden shrink-0 items-center gap-3 sm:flex">
          {token && canDeleteData && <DeleteDataButton />}
          <div className="flex items-center gap-2">
            {theme === 'light' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} />
          </div>
          {token && (
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Log out"
              title="Log out"
              className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogOut className="size-4" />
            </button>
          )}
        </div>
      </header>
      )}

      {/* Key on pathname only — query-param changes (e.g. OAuth callback
          cleanup via setSearchParams({}, { replace: true })) must NOT remount
          the subtree, otherwise modals flicker and the fade-in replays. */}
      <main className="flex-1 animate-fade-in" key={location.pathname}>
        {children}
      </main>
    </div>
  );
}

function ReportHeaderNav() {
  return (
    <div className="flex items-center gap-1">
      <span className="px-3 py-1.5 text-sm font-medium text-foreground">
        Strategic Report
      </span>
    </div>
  );
}
